var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { AvMicOffColor as AvMicOffColorIcon } from '@class-ui/icons/dist/AvMicOffColor';
import { AvMicOn as AvMicOnIcon } from '@class-ui/icons/dist/AvMicOn';
import { EmojiRaisedHandColor as EmojiRaisedHandColorIcon } from '@class-ui/icons/dist/EmojiRaisedHandColor';
import { EmojiStarColor as EmojiStarColorIcon } from '@class-ui/icons/dist/EmojiStarColor';
import { ClassThemeProvider } from '@class-ui/theme';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { grey, amber } from '@mui/material/colors';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { getInitials } from '../internal/getInitials';
import { OverflowTooltip } from '../OverflowTooltip';
const ButtonsContainer = styled(Box, {
    shouldForwardProp: (prop) => !['forceVisibleButtons', 'rightOffset'].includes(prop)
}) `
    position: absolute;
    top: 4px;
    right: ${(props) => (props.rightOffset ? '40px' : '4px')};
    display: flex;
    opacity: ${(props) => (props.forceVisibleButtons ? 1 : 0)};
`;
const Container = styled(Box, {
    shouldForwardProp: (prop) => !['borderStatusColor', 'borderThickness', 'isVideoOn'].includes(prop)
}) `
    border: ${(props) => props.borderThickness}px solid ${(props) => props.borderStatusColor};
    border-radius: 8px;
    background: ${(props) => (props.isVideoOn ? 'transparent' : grey[900])};
    position: relative;
    overflow: hidden;

    &:hover,
    &:focus-within {
        ${ButtonsContainer} {
            opacity: 1;
        }
    }
`;
const UserInfoContainer = styled(Box) `
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.8);
    color: ${(props) => props.theme.palette.primary.contrastText};
    padding: 2px 8px;
`;
const RoleBadge = styled(Typography, {
    shouldForwardProp: (prop) => !['roleColor'].includes(prop)
}) `
    background-color: ${(props) => props.roleColor};
    color: ${(props) => props.theme.palette.getContrastText(props.roleColor)};
    border-radius: 3px;
    padding: 0 5px;
    margin-right: 8px;
    line-height: 20px;
`;
const VideoAvatar = styled(Avatar) `
    width: 30%;
    aspect-ratio: 1;
    height: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${[grey[800]]};
`;
const AudioContainer = styled(Box) `
    margin-right: 8px;
    display: flex;
`;
const UnmuteButton = styled(Button) `
    margin-right: 8px;
    height: 30px;
`;
const MoreOptionsButton = styled(Button) `
    min-width: auto;
    padding: 0 6px;
    height: 30px;
`;
const UsernameCaption = styled(Typography) `
    line-height: 20px;
    max-width: 180px;
`;
const StarsBox = styled(Box) `
    width: fit-content;
    position: absolute;
    bottom: 30px;
    left: 12px;
`;
const HandRaise = styled(Box) `
    position: absolute;
    top: 4px;
    right: 4px;
    line-height: 0;
    padding: 2px;

    & .MuiSvgIcon-root {
        font-size: 28px;
        width: 28px;
        height: 28px;
    }
`;
const Feedback = styled(Box, { shouldForwardProp: (prop) => !['highlighted'].includes(prop) }) `
    position: absolute;
    top: 4px;
    left: 4px;
    line-height: 0;
    padding: 2px;
    border-radius: 8px;
    background-color: ${(props) => (props.highlighted ? props.theme.palette.primary.main : 'initial')};

    & .MuiSvgIcon-root {
        font-size: 28px;
        width: 28px;
        height: 28px;
    }
`;
const VisibilityHidden = styled(VisibilityOffOutlinedIcon) `
    color: ${[grey[400]]};
    transform: scaleX(-1);
    width: 20px;
    height: 20px;
    padding: 2px;
`;
const VideoFrameInner = (props) => {
    const theme = useTheme();
    const { displayName, avatarSrc, texts, showRoleBorder = true, isAudioOn = false, isSpeaking = false, isVideoOn = false, isHandRaised = false, isVisibilityHidden = false, feedback, feedbackHighlighted = false, role = 'student', children, onMoreOptionsClick, onAudioSwitchClick, forceVisibleButtons = false, starRating = 0, avatarProps } = props, other = __rest(props, ["displayName", "avatarSrc", "texts", "showRoleBorder", "isAudioOn", "isSpeaking", "isVideoOn", "isHandRaised", "isVisibilityHidden", "feedback", "feedbackHighlighted", "role", "children", "onMoreOptionsClick", "onAudioSwitchClick", "forceVisibleButtons", "starRating", "avatarProps"]);
    const roleColorMap = {
        student: grey[600],
        instructor: theme.palette.other.brandColor,
        assistant: theme.palette.primary.main,
        presenter: '#C700E6',
        'class-cam': theme.palette.success.main,
        'sign-language': theme.palette.success.main,
        'instructor-cam': theme.palette.success.main,
        unverified: theme.palette.error.main
    };
    const roleColor = roleColorMap[role];
    const starsMaxed = Math.min(starRating, 3);
    const borderThickness = useMemo(() => {
        if (isSpeaking) {
            return 2;
        }
        if (!showRoleBorder) {
            return 0;
        }
        return role === 'student' ? 1 : 2;
    }, [role, isSpeaking, showRoleBorder]);
    return (_jsxs(Container, Object.assign({ borderStatusColor: isSpeaking ? amber[400] : roleColor, borderThickness: borderThickness, isVideoOn: isVideoOn }, other, { children: [isVideoOn ? (children) : (_jsx(VideoAvatar, Object.assign({ src: avatarSrc, alt: displayName }, avatarProps, { children: getInitials(displayName) }))), _jsxs(ButtonsContainer, { forceVisibleButtons: forceVisibleButtons, rightOffset: isHandRaised, children: [onAudioSwitchClick && (_jsx(UnmuteButton, { variant: 'contained', color: 'primary', size: 'small', onClick: onAudioSwitchClick, children: isAudioOn ? texts['switch-audio-off'] : texts['switch-audio-on'] })), onMoreOptionsClick && (_jsx(Tooltip, { title: texts['more-options'], children: _jsx(MoreOptionsButton, { "aria-label": texts['more-options'], variant: 'contained', color: 'primary', size: 'small', onClick: onMoreOptionsClick, children: _jsx(MoreHorizIcon, {}) }) }))] }), _jsxs(UserInfoContainer, { children: [_jsx(AudioContainer, { children: isAudioOn ? _jsx(AvMicOnIcon, {}) : _jsx(AvMicOffColorIcon, {}) }), role !== 'student' && (_jsx(RoleBadge, { roleColor: roleColor, variant: 'caption', children: texts[`role-${role}`] })), _jsx(UsernameCaption, { variant: 'caption', children: _jsx(OverflowTooltip, { children: displayName }) }), isVisibilityHidden && (_jsx(Tooltip, { title: texts['visibility-hidden'], children: _jsx(VisibilityHidden, {}) }))] }), isHandRaised && (_jsx(Tooltip, { title: texts['hand-raised'], children: _jsx(HandRaise, { children: _jsx(EmojiRaisedHandColorIcon, { fontSize: 'large' }) }) })), Boolean(feedback) && (_jsx(Tooltip, { title: feedback.label, children: _jsx(Feedback, { highlighted: feedbackHighlighted, children: feedback.icon }) })), Boolean(starsMaxed) && (_jsx(Tooltip, { title: texts['star-rating'](starsMaxed), children: _jsx(StarsBox, { children: Array.from({ length: starsMaxed }, (_, i) => (_jsx(EmojiStarColorIcon, {}, i))) }) }))] })));
};
// Always force light theme
export const VideoFrame = (props) => (_jsx(ClassThemeProvider, { theme: 'light', cssReset: 'scoped', children: _jsx(VideoFrameInner, Object.assign({}, props)) }));
